import type {
  AnyParam,
  Context,
  CreateRoutes,
  FlatRoute,
  MatchResult,
  MatchRouteFn,
  ParseLocationFn,
  ParsePathFn,
  ParseQueryFn,
  RenderFn,
  RenderPathFn,
  RenderQueryFn,
  ReplaceFn,
  RouteNode,
  RouteNodeMap,
  RoutesProps,
  SafeParseLocationFn,
  SafeParsePathFn,
  SafeParseQueryFn,
  SafeParseResult,
  TemplateFn,
  WithContext,
} from "./types";

// 모든 라우트 정보를 저장하는 전역 맵
const routeRegistry = new Map<any, FlatRoute[]>();

export const createRoutes: CreateRoutes = (routeMap, options) => {
  const baseRoute = options?.basePath ? { path: options.basePath } : undefined;

  // 모든 라우트를 flat하게 수집
  const flatRoutes = collectFlatRoutes(
    routeMap,
    [],
    options?.baseUrl,
    options?.basePath ?? [],
  );

  // biome-ignore lint/suspicious/noExplicitAny: that's fine
  const proxy = (ctx: Context): any =>
    new Proxy(
      {
        "~context": ctx,
        "~routes": routeMap,
      },
      {
        get: (target, maybeRouteName, receiver) => {
          // 특정 프로퍼티들은 기본 동작으로 처리
          if (
            typeof maybeRouteName === "symbol" ||
            typeof maybeRouteName !== "string" ||
            maybeRouteName[0] === "~" ||
            maybeRouteName === "valueOf" ||
            maybeRouteName === "toString" ||
            maybeRouteName === "constructor" ||
            maybeRouteName === "prototype" ||
            maybeRouteName === "nodeType" ||
            maybeRouteName === "nodeName" ||
            maybeRouteName === "inspect" ||
            maybeRouteName.startsWith("Symbol.")
          ) {
            return Reflect.get(target, maybeRouteName, receiver);
          }

          // "_" 키나 라우트 맵에 존재하는 키는 처리
          if (
            maybeRouteName === "_" ||
            (ctx.children && maybeRouteName in ctx.children)
          ) {
            return proxy(addRoute(maybeRouteName, ctx));
          }

          // 존재하지 않는 라우트에 대해서는 undefined 반환
          return undefined;
        },
      }
    );

  const routes = proxy({
    isRelative: false,
    path: [],
    children: routeMap,
    nodes: baseRoute ? [baseRoute] : [],
    relativeNodes: baseRoute ? [baseRoute] : [],
    baseUrl: options?.baseUrl,
  });

  // 라우트 레지스트리에 flat 라우트들 저장
  routeRegistry.set(routes, flatRoutes);

  return routes;
};

const addRoute = (routeName: string, ctx: Context): Context => {
  if (routeName === "_") {
    return {
      ...ctx,
      path: ctx.path.concat(routeName),
      nodes: ctx.nodes.concat(ctx.nodes),
      relativeNodes: [],
      isRelative: true,
    };
  }
  const route = ctx.children?.[routeName];
  if (!route) {
    throw Error(
      `unknown segment ${routeName} in ${ctx.path.concat(routeName)}`
    );
  }
  return {
    ...ctx,
    path: ctx.path.concat(routeName),
    nodes: ctx.nodes.concat(route),
    relativeNodes: ctx.relativeNodes.concat(route),
    children: route.children,
  };
};

export const template: TemplateFn = ({
  "~context": { isRelative, relativeNodes },
}) => {
  const prefix = isRelative ? "" : "/";

  const serializedTemplate = relativeNodes
    .flatMap((route) => route.template ?? route.path ?? [])
    .map((segment) =>
      typeof segment === "string"
        ? segment
        : segment.options?.template ??
          `:${segment.name}${segment.kind === "optional" ? "?" : ""}`
    )
    .join("/");

  return prefix + serializedTemplate;
};

export const renderPath: RenderPathFn = (
  { "~context": { relativeNodes, isRelative, baseUrl } },
  // biome-ignore lint/suspicious/noExplicitAny: t
  params: Record<string, any>
) => {
  const serializedPath = relativeNodes
    .flatMap((route) => route.path ?? [])
    .flatMap((pathSegment) =>
      typeof pathSegment === "string"
        ? pathSegment
        : params[pathSegment.name] !== undefined
        ? pathSegment.parser.serialize(params[pathSegment.name])
        : pathSegment.options?.defaultValue
        ? pathSegment.parser.serialize(pathSegment.options.defaultValue())
        : []
    )
    .join("/");

  const prefix = isRelative ? "" : `${baseUrl ?? ""}/`;

  return prefix + serializedPath;
};

export const renderQuery: RenderQueryFn = (
  { "~context": { nodes } },
  // biome-ignore lint/suspicious/noExplicitAny: that's fine
  params: Record<string, any>
) => {
  const serializedQueryRecord: Record<string, string> = {};

  nodes
    .flatMap((route) => route.query ?? [])
    .forEach(({ name, parser }) => {
      if (params[name] !== undefined) {
        serializedQueryRecord[name] = parser.serialize(params[name]);
      }
    });

  return new URLSearchParams(serializedQueryRecord).toString();
};

export const render: RenderFn = (route, params) => {
  const pathname = renderPath(route, params.path);
  const searchParams = renderQuery(route, params.query);
  const separator = searchParams ? `?` : "";

  return pathname + separator + searchParams;
};

export const parsePath: ParsePathFn = (route, paramsOrPath) => {
  const params =
    typeof paramsOrPath === "string"
      ? paramsFromLocationPath(route, paramsOrPath).pathParams
      : paramsOrPath;

  const parsedParams: Record<string, unknown> = {};

  route["~context"].relativeNodes
    .flatMap((route) => route.path ?? [])
    .forEach((segment) => {
      if (typeof segment !== "string") {
        const value = params[segment.name];

        if (value !== undefined) {
          parsedParams[segment.name] = segment.parser.parse(value);
        } else if (segment.kind === "required") {
          throw Error(
            `parsePath: required path parameter "${
              segment.name
            }" was not provided in "${template(route)}"`
          );
        }
      }
    });

  // biome-ignore lint/suspicious/noExplicitAny: that's fine
  return parsedParams as any;
};

export const parseQuery: ParseQueryFn = (route, paramsOrQuery) => {
  const params =
    typeof paramsOrQuery === "string"
      ? paramsFromQuery(paramsOrQuery)
      : paramsOrQuery;

  const parsedQuery: Record<string, unknown> = {};

  route["~context"].nodes
    .flatMap((route) => route.query ?? [])
    .forEach((segment) => {
      const value = params[segment.name];
      if (value != null) {
        parsedQuery[segment.name] = segment.parser.parse(value);
      } else if (segment.kind === "required") {
        throw Error(
          `parseQuery: required query parameter "${
            segment.name
          }" was not provided in "${template(route)}"`
        );
      }
    });

  // biome-ignore lint/suspicious/noExplicitAny: that's fine
  return parsedQuery as any;
};

export const parseLocation: ParseLocationFn = (route, paramsOrLocation) => {
  const [pathParams, queryParams] =
    typeof paramsOrLocation === "string"
      ? paramsOrLocation.split("&")
      : [paramsOrLocation, paramsOrLocation];

  return {
    path: parsePath(route, pathParams),
    query: parseQuery(route, queryParams),
  };
};

export const replace: ReplaceFn = (
  route,
  location,
  // biome-ignore lint/suspicious/noExplicitAny: that's fine
  params: Record<string, any>
) => {
  const [locationPath = "", locationQuery = ""] = location.split("?");

  const { pathParams, remainingSegments } = paramsFromLocationPath(
    route,
    locationPath
  );

  const {
    "~context": { isRelative, relativeNodes, nodes },
  } = route;

  const pathname = relativeNodes
    .flatMap((route) => route.path ?? [])
    .flatMap((pathSegment) => {
      if (typeof pathSegment === "string") return pathSegment;

      const { name, kind, parser } = pathSegment;

      if (params["path"] && name in params["path"]) {
        if (typeof params["path"][name] !== "undefined")
          return parser.serialize(params["path"][name]);

        if (kind === "required") {
          throw Error(
            `replace: required path param ${name} can't be set to undefined`
          );
        }
        return [];
      }

      return pathParams[name] ?? [];
    })
    .concat(remainingSegments)
    .join("/");

  const queryParams = paramsFromQuery(locationQuery);

  if (params["query"]) {
    nodes
      .flatMap((r) => r.query ?? [])
      .forEach(({ name, parser, kind }) => {
        if (typeof params["query"][name] !== "undefined") {
          queryParams[name] = parser.serialize(params["query"][name]);
        } else if (name in params["query"]) {
          if (kind === "required") {
            throw Error(
              `replace: required query param ${name} can't be set to undefined`
            );
          } else {
            delete queryParams[name];
          }
        }
      });
  }

  const prefix = isRelative ? "" : "/";
  const searchParams = new URLSearchParams(queryParams).toString();
  const separator = searchParams ? `?` : "";

  return prefix + pathname + separator + searchParams;
};

export const paramsFromQuery = (query: string) =>
  Object.fromEntries(new URLSearchParams(query));

export const paramsFromLocationPath = (
  route: WithContext,
  locationPath: string = ""
) => {
  const remainingSegments = locationPath
    .slice(locationPath[0] === "/" ? 1 : 0)
    .split("/");

  const pathParams: Record<string, string> = {};

  // keep track of recent optional params since they might contain path segments
  // if a path segment doesn't match the algorithm starts backtracking in this array
  const recentOptionalParams: string[] = [];

  route["~context"].relativeNodes
    .flatMap((r) => r.path ?? [])
    .forEach((segment) => {
      const locationPathSegment = remainingSegments.shift();

      if (typeof segment === "string") {
        if (segment === locationPathSegment) {
          recentOptionalParams.length = 0; // irrelevant from here
        } else {
          // segment might have been swallowed by an optional param
          let recentParam: string | undefined;
          let foundMatch = false;
          // biome-ignore lint/suspicious/noAssignInExpressions: no this is not confusing
          while ((recentParam = recentOptionalParams.shift())) {
            if (pathParams[recentParam] === segment) {
              delete pathParams[recentParam];
              // hold segment back for the next iteration
              locationPathSegment &&
                remainingSegments.unshift(locationPathSegment);
              foundMatch = true;
            }
          }
          if (!foundMatch) {
            throw new Error(
              `"${locationPath}" doesn't match "${template(
                route
              )}", missing segment "${segment}"`
            );
          }
        }
      } else {
        if (locationPathSegment != null) {
          pathParams[segment.name] = locationPathSegment;
          if (segment.kind === "optional") {
            recentOptionalParams.push(segment.name);
          } else {
            recentOptionalParams.length = 0;
          }
        } else if (segment.kind === "required") {
          throw new Error(
            `"${locationPath}" doesn't match "${template(
              route
            )}", missing parameter "${segment.name}"`
          );
        }
      }
    });

  return {
    pathParams,
    remainingSegments,
  };
};

export const safeCall =
  // biome-ignore lint/suspicious/noExplicitAny: that's fine


    <T extends (...args: any[]) => any>(fn: T) =>
    // biome-ignore lint/suspicious/noExplicitAny: that's fine
    (...params: any[]): SafeParseResult<any> => {
      try {
        const result = fn(...params);
        return {
          success: true,
          data: result,
        };
      } catch (err: unknown) {
        return {
          success: false,
          error:
            err instanceof Error
              ? err
              : new Error(err === "string" ? err : `unknown error: ${err}`),
        };
      }
    };

export const safeParsePath: SafeParsePathFn = safeCall(parsePath);
export const safeParseQuery: SafeParseQueryFn = safeCall(parseQuery);
export const safeParseLocation: SafeParseLocationFn = safeCall(parseLocation);

// 라우트를 flat하게 수집하는 함수
const collectFlatRoutes = (
  routeMap: RouteNodeMap,
  parentPath: string[] = [],
  baseUrl?: string,
  parentPathSegments: (string | AnyParam)[] = [],
  parentQuerySegments: AnyParam[] = []
): FlatRoute[] => {
  const flatRoutes: FlatRoute[] = [];

  for (const [routeName, routeNode] of Object.entries(routeMap)) {
    const currentPath = [...parentPath, routeName];
    const currentPathSegments = [
      ...parentPathSegments,
      ...(routeNode.path || []),
    ];
    const currentQuerySegments = [
      ...parentQuerySegments,
      ...(routeNode.query || []),
    ];

    // 현재 라우트 추가
    const template = generateTemplate(currentPathSegments, baseUrl);
    flatRoutes.push({
      path: currentPath,
      pathSegments: currentPathSegments,
      querySegments: currentQuerySegments,
      template,
      routeKey: currentPath.join("."),
    });

    // 자식 라우트들 재귀적으로 수집
    if (routeNode.children) {
      flatRoutes.push(
        ...collectFlatRoutes(
          routeNode.children,
          currentPath,
          baseUrl,
          currentPathSegments,
          currentQuerySegments
        )
      );
    }
  }

  return flatRoutes;
};

// 템플릿 생성 함수
const generateTemplate = (
  pathSegments: (string | AnyParam)[],
  baseUrl?: string
): string => {
  const prefix = baseUrl ? baseUrl : "";

  if (pathSegments.length === 0) {
    return prefix + "/";
  }

  const templateParts = pathSegments.map((segment) =>
    typeof segment === "string"
      ? segment
      : segment.options?.template ??
        `:${segment.name}${segment.kind === "optional" ? "?" : ""}`
  );

  return prefix + "/" + templateParts.join("/");
};

export const matchRoute: MatchRouteFn = <
  Routes extends RouteNodeMap,
  Nodes extends RouteNode[] = RouteNode[],
  RelativeNodes extends RouteNode[] = RouteNode[]
>(
  routes: RoutesProps<Routes, Nodes, RelativeNodes>,
  url: string
): MatchResult<Routes> | null => {
  const [pathname = "", search = ""] = url.split("?");

  // 레지스트리에서 flat 라우트들 가져오기
  const flatRoutes = routeRegistry.get(routes);
  if (!flatRoutes) {
    return null;
  }

  // 더 구체적인 라우트부터 매칭 시도 (경로 세그먼트가 더 많은 것부터)
  const sortedRoutes = flatRoutes.sort(
    (a, b) => b.pathSegments.length - a.pathSegments.length
  );

  for (const flatRoute of sortedRoutes) {
    const match = tryMatchFlatRoute(flatRoute, pathname, search, routes);
    if (match) {
      return match;
    }
  }

  return null;
};

export type IsRouteActiveOptions = {
  /**
   * - false (default): 현재 라우트가 targetRoute의 "하위" 라우트여도 active로 처리합니다. (prefix match)
   * - true: 정확히 targetRoute와 동일한 라우트일 때만 active로 처리합니다. (exact match)
   */
  exact?: boolean;
};

const normalizeRoutePathSegments = (segments: string[]) =>
  segments.filter((segment) => segment !== "_");

const isPathPrefix = (fullPath: string[], prefixPath: string[]) => {
  if (prefixPath.length === 0) return true;
  if (fullPath.length < prefixPath.length) return false;

  return prefixPath.every((segment, index) => fullPath[index] === segment);
};

/**
 * 현재 pathname이 특정 route(및 그 하위 route)에 속하는지 판별합니다.
 * 문자열 기반(active href 비교) 대신 `matchRoute()` 결과의 route path(prefix)를 사용합니다.
 */
export const isRouteActive = <
  Routes extends RouteNodeMap,
  Nodes extends RouteNode[] = RouteNode[],
  RelativeNodes extends RouteNode[] = RouteNode[]
>(
  routes: RoutesProps<Routes, Nodes, RelativeNodes>,
  pathname: string | null,
  targetRoute: WithContext,
  options?: IsRouteActiveOptions
): boolean => {
  if (pathname === null) return false;

  const currentMatch = matchRoute(routes, pathname);
  if (currentMatch === null) return false;

  const currentPath = normalizeRoutePathSegments(currentMatch.path);
  const targetPath = normalizeRoutePathSegments(targetRoute["~context"].path);

  if (options?.exact) {
    return (
      currentPath.length === targetPath.length &&
      isPathPrefix(currentPath, targetPath)
    );
  }

  return isPathPrefix(currentPath, targetPath);
};

// flat 라우트와 매칭을 시도하는 함수
const tryMatchFlatRoute = (
  flatRoute: FlatRoute,
  pathname: string,
  search: string,
  routes: any
): MatchResult<any> | null => {
  const pathParts = pathname === "/" ? [] : pathname.split("/").filter(Boolean);
  const { pathSegments } = flatRoute;

  // 경로 세그먼트 수가 맞지 않으면 실패
  const requiredSegments = pathSegments.filter(
    (seg) =>
      typeof seg === "string" ||
      (typeof seg === "object" && seg && seg.kind === "required")
  );

  if (
    pathParts.length < requiredSegments.length ||
    pathParts.length > pathSegments.length
  ) {
    return null;
  }

  const pathParams: Record<string, string> = {};
  let pathIndex = 0;

  // 각 세그먼트 매칭
  for (const segment of pathSegments) {
    if (typeof segment === "string") {
      if (pathIndex >= pathParts.length || pathParts[pathIndex] !== segment) {
        return null;
      }
      pathIndex++;
    } else if (segment && typeof segment === "object") {
      // 파라미터 세그먼트
      if (pathIndex < pathParts.length) {
        const paramValue = pathParts[pathIndex];
        if (paramValue === undefined) return null;

        // 파라미터 파싱 유효성 검사
        try {
          segment.parser.parse(paramValue);
          pathParams[segment.name] = paramValue;
          pathIndex++;
        } catch {
          if (segment.kind === "optional") {
            continue;
          }

          // 파싱 실패시 매칭 실패
          return null;
        }
      } else if (segment.kind === "required") {
        return null;
      }
    }
  }

  // 남은 경로 부분이 있으면 매칭 실패
  if (pathIndex < pathParts.length) {
    return null;
  }

  const queryParams = paramsFromQuery(search);

  // 실제 라우트 객체를 찾기 위해 경로를 따라 이동
  let currentRoute = routes;
  for (const pathSegment of flatRoute.path) {
    try {
      currentRoute = currentRoute[pathSegment];
      if (!currentRoute) break;
    } catch {
      break;
    }
  }

  return {
    route: currentRoute || routes,
    pathParams,
    queryParams,
    template: flatRoute.template,
    path: flatRoute.path,
  };
};
